import type { PortfolioMandate } from 'shared';
import { PORTFOLIO_MANDATES } from 'shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface RememberedAccount {
  id: string;
  name: string;
  broker: string;
  currency: string;
  mandate: PortfolioMandate;
}

export type HoldingsLayout = 'cards' | 'list';

interface UiState {
  executionMarkersVisible: boolean;
  sidebarOpen: boolean;
  theme: 'dark' | 'light';
  lastPrimaryAccount: RememberedAccount | null;
  holdingsLayout: HoldingsLayout;
  setExecutionMarkersVisible: (visible: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setLastPrimaryAccount: (account: RememberedAccount | null) => void;
  setHoldingsLayout: (layout: HoldingsLayout) => void;
}

function sameAccount(left: RememberedAccount | null, right: RememberedAccount | null): boolean {
  if (left === null || right === null) return left === right;
  return (
    left.id === right.id &&
    left.name === right.name &&
    left.broker === right.broker &&
    left.currency === right.currency &&
    left.mandate === right.mandate
  );
}

function stringField(record: object, key: string): string | null {
  const value: unknown = Reflect.get(record, key);
  return typeof value === 'string' ? value : null;
}

function rememberedAccountFrom(persisted: unknown): RememberedAccount | null {
  if (typeof persisted !== 'object' || persisted === null) return null;
  const account: unknown = Reflect.get(persisted, 'lastPrimaryAccount');
  if (typeof account !== 'object' || account === null) return null;
  const id = stringField(account, 'id');
  const name = stringField(account, 'name');
  const broker = stringField(account, 'broker');
  const currency = stringField(account, 'currency');
  const mandate = PORTFOLIO_MANDATES.find(
    (candidate) => candidate === stringField(account, 'mandate'),
  );
  if (id === null || name === null || broker === null || currency === null || !mandate) {
    return null;
  }
  return { id, name, broker, currency, mandate };
}

function holdingsLayoutFrom(persisted: unknown): HoldingsLayout {
  if (typeof persisted !== 'object' || persisted === null) return 'cards';
  return Reflect.get(persisted, 'holdingsLayout') === 'list' ? 'list' : 'cards';
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      executionMarkersVisible: true,
      sidebarOpen: false,
      theme: 'dark',
      lastPrimaryAccount: null,
      holdingsLayout: 'cards',
      setExecutionMarkersVisible: (visible) => set({ executionMarkersVisible: visible }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setTheme: (theme) => set({ theme }),
      setLastPrimaryAccount: (account) => {
        if (!sameAccount(get().lastPrimaryAccount, account)) set({ lastPrimaryAccount: account });
      },
      setHoldingsLayout: (layout) => set({ holdingsLayout: layout }),
    }),
    {
      name: 'disciplined-trader-ui',
      storage: createJSONStorage(() => localStorage),
      // The last-used account lets the dashboard load before /accounts returns.
      partialize: (state) => ({
        lastPrimaryAccount: state.lastPrimaryAccount,
        holdingsLayout: state.holdingsLayout,
      }),
      merge: (persisted, current) => ({
        ...current,
        lastPrimaryAccount: rememberedAccountFrom(persisted),
        holdingsLayout: holdingsLayoutFrom(persisted),
      }),
    },
  ),
);
